import type { AIMessageDraft } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = (value: string): string => value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);

// Escape characters that would break out of a `href="..."`. Plain HTML escape
// is enough because the value is always quoted with double quotes below.
const escapeAttr = (value: string): string => escapeHtml(value);

// Render the plain-text message body as paragraphs + simple bullet list, the
// way the design mock shows it. Lines beginning with "•" or "-" become list
// items; blank lines split paragraphs; everything else is wrapped in a <p>.
const renderBody = (message: string): string => {
  const lines = message.split('\n');
  const blocks: string[] = [];
  let currentParagraph: string[] = [];
  let currentList: string[] = [];

  const flushParagraph = () => {
    if (currentParagraph.length > 0) {
      blocks.push(`<p style="margin:0 0 16px 0;">${currentParagraph.join('<br />')}</p>`);
      currentParagraph = [];
    }
  };
  const flushList = () => {
    if (currentList.length > 0) {
      blocks.push(
        `<ul style="margin:0 0 16px 0;padding-left:20px;">${currentList
          .map((item) => `<li style="margin:0 0 4px 0;">${item}</li>`)
          .join('')}</ul>`
      );
      currentList = [];
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line === '') {
      flushParagraph();
      flushList();
    } else {
      const bulletMatch = line.match(/^(?:•|-)\s*(.*)$/);
      if (bulletMatch) {
        flushParagraph();
        currentList.push(escapeHtml(bulletMatch[1] ?? ''));
      } else {
        flushList();
        currentParagraph.push(escapeHtml(line));
      }
    }
  }
  flushParagraph();
  flushList();
  return blocks.join('');
};

/**
 * Stand-in for the backend-compiled email HTML. Once the messaging service
 * returns ready-to-render HTML, replace the entire body of this function
 * with `return draft.compiledHtml` (or whatever the field is called); the
 * call sites and iframe rendering stay the same.
 *
 * The HTML is inline-styled so it renders identically inside a sandboxed
 * iframe with no external stylesheet.
 */
export const mockCompileEmailHtml = (draft: AIMessageDraft): string => {
  const subject = escapeHtml(draft.subject);
  const ctaText = escapeHtml(draft.ctaText);
  // Reject anything that isn't http/https so a `javascript:` URL can't appear
  // verbatim in the rendered href even if the iframe sandbox is ever relaxed.
  const isSafeUrl = /^https?:\/\//i.test(draft.ctaUrl);
  const ctaUrl = isSafeUrl ? escapeAttr(draft.ctaUrl) : '#';
  const body = renderBody(draft.message);

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${subject}</title>
</head>
<body style="margin:0;padding:24px;background:#f5f7fa;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#1f1f1f;">
<div style="max-width:520px;margin:0 auto;background:#ffffff;border-radius:8px;padding:32px;">
  <div style="text-align:center;margin-bottom:24px;">
    <span style="display:inline-block;font-family:Georgia,serif;font-size:22px;font-weight:700;color:#0056d2;letter-spacing:0.5px;">coursera</span>
  </div>
  <div style="font-size:14px;line-height:22px;color:#1f1f1f;">
    ${body}
  </div>
  <div style="margin-top:8px;">
    <a href="${ctaUrl}" style="display:inline-block;background:#0056d2;color:#ffffff;text-decoration:none;font-weight:600;font-size:14px;line-height:20px;padding:10px 20px;border-radius:4px;">${ctaText}</a>
  </div>
  <p style="margin-top:24px;font-size:11px;line-height:16px;color:#6a6a6a;">© Your Organization. You're receiving this email because you're enrolled in a learning program. <a href="#" style="color:#6a6a6a;text-decoration:underline;">Unsubscribe</a>.</p>
</div>
</body>
</html>`;
};
